import { defineStore } from 'pinia'
import { useAsyncState } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { isObject } from 'lodash-es'
import { apiClientV1, apiClientV2 } from '@/ContextTab/services/apiClient.ts'
import { ROUTE_MAP } from '@/router'
import { GatewaySubType } from '@/Workers/Gateway/gwService/constants.ts'
import { useGatewayStore } from '@/ContextTab/stores/gateway.ts'
import { ENABLE_NEW_EDITOR } from '@/config'
import { openedLegacyTabs } from '@/LegacyApp/src/services/openedLegacyTabs'
import { legacyBridge } from '@/services/legacyBridge.ts'
import type { NewsDetailed, NewsVersionType } from '@/api/generated/v2'


/* Стор для новости в редакторе, стор всегда должен состоять из 1 новости,
каждое следующее открытие новости перезаписывает текущий стейт новости */
export const useNewsEditorStore = defineStore('newsEditorStore', () => {
  const gatewayStore = useGatewayStore()
  const router = useRouter()

  const {
    state: newsItem,
    isLoading,
    error,
    execute: loadNewsById,
  } = useAsyncState(
    async (news_id: number) => {
      const response = await apiClientV2.NewsService.newsNewsNewsIdGet({
        path: {
          news_id,
        },
      })

      if (response.error) {
        throw new Error('Не удалось загрузить новость')
      }

      return response.data
    },
    undefined,
    {
      immediate: false,
    },
  )

  const subscribeGateway = (news_id: number) => gatewayStore.subscribe(GatewaySubType.SPECIFIC_NEWS, {
    news_id,
  }, (message) => onGwNewsMessage(message, news_id))

  const unsubscribeGateway = (news_id: number) => gatewayStore.unsubscribe(GatewaySubType.SPECIFIC_NEWS, {
    news_id,
  })

  // Приватная функция для обработки сообщений от GW
  function onGwNewsMessage(message: any, newsId: number) {
    if (message.payload.data.news.id !== newsId) {
      // Игнорируем сообщения, которые не относятся к текущей новости
      return
    }
    /* TODO: Не совпадает полностью тип NewsDetailed, потому что данные сериализуются по api V1,
    *   из-за этого не хватает некоторых полей, например (is_read и can_work), при переводе сокета на
    *   сериализацию на основе api v2 эта проблема уйдет */
    newsItem.value = message.payload.data.news
  }

  async function open(id: number) {
    /* Если активен флаг нового редактора, то открываем в нем, иначе
    * делаем логику поиска открытой вкладки старого редактора */
    if (ENABLE_NEW_EDITOR) {
      router.push({ name: ROUTE_MAP.newsEditorById, params: { id } })
    } else {
      if (!openedLegacyTabs.hasEditorTabs()) {
        window.open(`${window.location.origin}/legacy/news/${id}`)
      } else {
        // if legacy exists, it will try to focus and open at
        // src/LegacyApp/src/store/plugins/psUiBridgePlugin.js
        const opened = await legacyBridge.openNewsItem(id, window.contextId)
        if (!opened) {
          // no editor tabs found, or they stuck
          window.open(`${window.location.origin}/legacy/news/${id}`)
        }
      }
    }
  }

  function openInNewTab(id: number) {
    const routeData = router.resolve({ name: ROUTE_MAP.newsEditorById, params: { id } })
    window.open(routeData.href, '_blank')
  }

  function openByNewsItem(news: unknown) {
    if (isObject(news) && 'id' in news && typeof news.id === 'number') {
      open(news.id)
    }
  }

  async function create(type_id: NewsVersionType) {
    const res = await apiClientV1.NewsService.postNews({
      body: {
        type_id,
      },
    })
    if (!res.data) {
      throw new Error('Запрос на создание новости завершился с ошибкой')
    }

    return res.data.data
  }

  async function pick(news_id: number) {
    const res = await apiClientV2.NewsService.pickNewsNewsIdPickPost({
      path: {
        news_id,
      },
    })
    if (!res.data) {
      throw new Error('Запрос на взятие новости в работу завершился с ошибкой')
    }

    return res.data
  }

  async function extend(news_id: number) {
    const res = await apiClientV1.NewsService.postNewsByNewsIdExtend({
      path: {
        news_id,
      },
    })
    if (!res.data) {
      throw new Error('Запрос на расширение новости завершился с ошибкой')
    }

    return res.data.data
  }

  async function republish(news_id: number) {
    const res = await apiClientV1.NewsService.postNewsByNewsIdRepublish({
      path: {
        news_id,
      },
    })
    if (!res.data) {
      throw new Error('Запрос на создание повтора новости завершился с ошибкой')
    }

    return res.data.data
  }

  async function republishUnedited(news_id: number, codes: number[]) {
    const res = await apiClientV1.NewsService.postNewsByNewsIdRepublishUnedited({
      path: {
        news_id,
      },
      body: {
        codes: codes.map((id) => ({ id })),
      },
    })
    if (!res.data) {
      throw new Error('Запрос на переопубликование без редактирования завершился с ошибкой')
    }

    return res.data.data
  }

  async function revoke(news_id: number) {
    const res = await apiClientV1.NewsService.postNewsByNewsIdRevoke({
      path: {
        news_id,
      },
    })
    if (!res.data) {
      throw new Error('Запрос на аннулирование новости завершился с ошибкой')
    }

    return res.data.data
  }

  async function deleteNews(news_id: number) {
    const res = await apiClientV1.NewsService.deleteNewsByNewsId({
      path: {
        news_id,
      },
    })

    return res.data
  }

  async function dispose(newsId: number, reason: string | null | undefined, newVersion: Partial<NewsDetailed> = {}) {
    const res = await apiClientV1.NewsService.postNewsDispose({
      body: [{
        /* TODO: Убрать as any при переводе на api v2 */
        ...newVersion.version as any,
        id: newsId,
        reason,
      }],
    })
    if (!res.data) {
      throw new Error('Ошибка при отправке новости в отвал')
    }

    return res.data.data
  }

  function resetCurrentNews() {
    newsItem.value = undefined
    router.push({ name: ROUTE_MAP.newsEditorById })
  }

  return {
    newsItem,
    isLoading,
    error,
    loadNewsById,

    subscribeGateway,
    unsubscribeGateway,

    open,
    openInNewTab,
    openByNewsItem,
    create,
    pick,
    extend,
    republish,
    republishUnedited,
    revoke,
    deleteNews,
    dispose,
    resetCurrentNews,
  }
})
